import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api';
import NotificationBell from './NotificationBell';

export default function Topbar({ onSearch, searchQuery, user, setUser, setView, onReviewClick, onHomeClick, darkMode, toggleDarkMode }) {
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const isGuest = user?.isAnonymous || user?.provider === 'anon';

  // Sync clear from outside (e.g. navigating away)
  React.useEffect(() => {
    if (!searchQuery) setInputValue('');
  }, [searchQuery]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
    onSearch && onSearch(e.target.value);
  };

  const handleClear = () => {
    setInputValue('');
    onSearch && onSearch('');
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setShowDropdown(false);
      setUser(null);
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      // Still clear local state
      setUser(null);
      navigate('/login');
    }
  };

  const initial = user ? (user.displayName || user.name || '').charAt(0).toUpperCase() : '?';

  return (
    <nav className="desktop-topbar" style={{
      position: 'sticky', top: 0, zIndex: 900,
      background: 'var(--bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center',
      height: 52, padding: '0 24px',
    }}>
      {/* Logo */}
      <a href="/" onClick={e => { e.preventDefault(); navigate('/'); if (onHomeClick) onHomeClick(); else if (setView) setView('home'); }} style={{
        display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0,
      }}>
        <img src="/logo-mascot.png" alt="HireKahani Elephant" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20, color: 'var(--blue)', letterSpacing: -0.5 }}>
            Hire<span style={{ color: 'var(--text)' }}>Kahani</span>
          </span>
        </div>
      </a>

      {/* Search (Flexible Center) */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', margin: '0 20px' }} className="mobile-hide topbar-search-center">
        <div style={{ position: 'relative', width: '100%', maxWidth: 500 }}>
          <svg style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={inputValue}
            placeholder="Search company, role, city…"
            style={{
              background: searchFocused ? 'var(--surface)' : 'var(--surface2)',
              border: `1px solid ${searchFocused ? 'var(--blue)' : 'var(--border)'}`,
              borderRadius: 10, color: 'var(--text)', fontFamily: 'var(--sans)',
              fontSize: 13, padding: '8px 36px 8px 40px',
              width: '100%', outline: 'none', transition: 'all 0.25s',
              boxShadow: searchFocused ? '0 4px 12px rgba(14,165,233,0.1)' : 'none',
            }}
            onChange={handleChange}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {inputValue && (
            <button
              onClick={handleClear}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text3)', fontSize: 16, lineHeight: 1, padding: 2,
              }}
            >×</button>
          )}
        </div>

      </div>

      {/* Mobile Search Button (Visible only on mobile) */}
      <div style={{ display: 'none', marginLeft: 'auto', marginRight: 12 }} className="mobile-show">
        <button style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
        </button>
      </div>

      {/* Right */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Dark mode toggle */}
        <button
          onClick={toggleDarkMode}
          title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 8, width: 34, height: 34, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text2)', transition: 'all 0.15s', flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          {darkMode ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        {/* Notification Bell — signed-in users only */}
        {!user?.isAnonymous && <NotificationBell user={user} onReviewClick={onReviewClick} />}
        {/* My Kahani */}
        <button
          data-tour="share"
          onClick={() => setView('my-kahani')}
          style={{
            background: 'var(--blue)', color: '#fff', fontFamily: 'var(--sans)',
            fontSize: 14, fontWeight: 700, padding: '10px 24px', borderRadius: 8,
            border: 'none', cursor: 'pointer', transition: 'all 0.15s',
            letterSpacing: 0.5, boxShadow: '0 4px 10px rgba(14,165,233,0.2)'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#0284c7'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--blue)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          My Kahani
        </button>

        {/* Avatar dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowDropdown(d => !d)}
            style={{
              width: 38, height: 38, borderRadius: '50%',
              background: user?.provider === 'anon' ? 'var(--text3)' : 'var(--blue)',
              color: '#fff', border: 'var(--border) 2px solid', cursor: 'pointer',
              fontFamily: 'var(--display)', fontWeight: 700, fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {initial}
          </button>

          {showDropdown && (
            <div style={{
              position: 'absolute', right: 0, top: 44,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
              boxShadow: '0 8px 16px rgba(0,0,0,0.08)', width: 160, overflow: 'hidden', zIndex: 999,
              animation: 'fadeUp 0.15s ease-out'
            }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
                <div style={{ fontWeight: 800, fontSize: 11, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isGuest ? 'Guest' : (user?.nickname || user?.hkId || user?.displayName || user?.name)}
                </div>
                {isGuest && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Browsing anonymously</div>
                )}
              </div>
              <div style={{ borderTop: '1px solid var(--border)' }}>
                {isGuest ? (
                  <DropdownItem
                    label="Sign In / Sign Up"
                    onClick={() => { setShowDropdown(false); setUser(null); }}
                  />
                ) : (
                  <>
                    <DropdownItem label="My Profile" onClick={() => { setShowDropdown(false); navigate('/profile'); }} />
                    <DropdownItem label="Sign out" onClick={handleSignOut} danger />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function DropdownItem({ label, onClick, danger }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', textAlign: 'left', padding: '8px 12px',
        background: hov ? (danger ? 'rgba(239,68,68,0.06)' : 'var(--surface2)') : 'transparent',
        border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)',
        fontSize: 12, color: danger ? 'var(--red)' : 'var(--text)', fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}
